import React, { useState, useRef, useEffect } from 'react';
import { Box, BoxProps } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';

interface LazyYouTubeProps extends Omit<BoxProps, 'component'> {
  videoId: string;
  title?: string;
  thumbnailUrl?: string;
  autoplay?: boolean;
  /**
   * Above-the-fold hero videos. With `autoplay`, the video still autoplays
   * on land — but the heavy YouTube player iframe is deferred until the
   * browser is idle, so it doesn't block first paint / LCP. The poster
   * thumbnail shows instantly; the iframe swaps in a beat later.
   */
  priority?: boolean;
  width?: number | string;
  height?: number | string;
  onLoad?: () => void;
}

/**
 * Lazy-loaded YouTube embed using the facade pattern.
 *
 * - Default: shows a thumbnail with a play button; the ~1.4 MB YouTube
 *   player only loads when the user clicks.
 * - `priority` + `autoplay` (hero videos): the thumbnail paints
 *   immediately, then the autoplaying iframe is mounted on the first idle
 *   frame — autoplay-on-land is preserved, but YouTube's player JS is kept
 *   off the critical rendering path.
 */
const LazyYouTube: React.FC<LazyYouTubeProps> = ({
  videoId,
  title = 'YouTube video',
  thumbnailUrl,
  autoplay = false,
  priority = false,
  width = '100%',
  height = '315',
  onLoad,
  sx,
  ...boxProps
}) => {
  // Hero autoplay videos defer the iframe (see below); everything else
  // starts on the thumbnail and loads on click.
  const deferredAutoplay = priority && autoplay;
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // For hero autoplay videos: mount the autoplaying iframe once the browser
  // is idle, so the player's JS doesn't compete with first paint / LCP.
  // requestIdleCallback isn't in Safari yet, so it's treated as optional
  // here with a setTimeout fallback.
  useEffect(() => {
    if (!deferredAutoplay) return;

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const mount = () => {
      setIsLoaded(true);
      onLoad?.();
    };

    if (w.requestIdleCallback) {
      idleId = w.requestIdleCallback(mount, { timeout: 2500 });
    } else {
      timeoutId = window.setTimeout(mount, 1200);
    }

    return () => {
      if (idleId !== undefined && w.cancelIdleCallback) {
        w.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [deferredAutoplay, onLoad]);

  // Generate thumbnail URL if not provided
  const defaultThumbnail = thumbnailUrl || `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;

  // YouTube embed URL with privacy-enhanced mode (no-cookie domain).
  // autoplay requires mute=1 for most browsers (YouTube policy).
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&mute=${autoplay ? 1 : 0}&rel=0&modestbranding=1&enablejsapi=1&loading=lazy`;

  const handleClick = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // The thumbnail is only an interactive "play" affordance for the
  // click-to-load facade. For a hero video that will autoplay on its own,
  // it's just a transient poster — no button chrome, no click handler.
  const isClickFacade = !isLoaded && !deferredAutoplay;

  return (
    <Box
      ref={containerRef}
      {...boxProps}
      sx={{
        position: 'relative',
        width,
        height,
        overflow: 'hidden',
        cursor: isClickFacade ? 'pointer' : 'default',
        ...sx,
      }}
      onClick={isClickFacade ? handleClick : undefined}
      onKeyPress={isClickFacade ? handleKeyPress : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role={isClickFacade ? 'button' : undefined}
      tabIndex={isClickFacade ? 0 : undefined}
      aria-label={isClickFacade ? `Play video: ${title}` : undefined}
    >
      {!isLoaded ? (
        // Poster thumbnail. Eager-loaded for hero videos (it's the LCP
        // candidate); lazy otherwise.
        (<>
      <Box width={typeof width === 'number' ? width : undefined} height={typeof height === 'number' ? height : undefined}
            component="img"
            src={defaultThumbnail}
            alt={title}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
            decoding="async"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.3s ease',
              transform: isHovered && isClickFacade ? 'scale(1.05)' : 'scale(1)'
            }} />
          {/* Play button + YouTube badge only for the click-to-load facade. */}
          {isClickFacade && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  borderRadius: '50%',
                  width: 68,
                  height: 68,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 0, 0, 0.9)',
                    transform: 'translate(-50%, -50%) scale(1.1)',
                  },
                }}
              >
                <PlayArrow sx={{ color: 'white', fontSize: 36, ml: 0.5 }} />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                YouTube
              </Box>
            </>
          )}
        </>)
      ) : (
        // Loaded iframe
        (<Box
          component="iframe"
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />)
      )}
    </Box>
  );
};

export default LazyYouTube;
